/**
 * LSA Simple Direct Guidance - No Mappings, No Complexity
 * Every requirement ID maps directly to its guidance  
 * Based on Regulations Assistant verified guidance
 */

export const LSA_SIMPLE_GUIDANCE = {
  // Life Jackets
  'lsa.lifejackets.adult': {
    id: 'lsa.lifejackets.adult',
    name: 'Adult Life Jackets',
    category: 'LSA',
    regulation: 'WBC3 Section 14.1',
    description: 'Adult life jackets for crew and passengers.',
    authority: 'WBC3 Section 14.1',
    placement: {
      requirements: [
        'One per person on board plus 10% spare',
        'Adult life jackets for crew and passengers',
        'Must be readily accessible'
      ]
    },
    specifications: {
      type: 'Adult life jackets (150N minimum)',
      standards: 'WBC3 Section 14.1 compliance required',
      mounting: 'Readily accessible storage locations'
    },
    maintenance: {
      mandatory: [
        'Annual inspection required',
        'Monthly visual check',
        'Inflation system test',
        'Light and whistle functionality'
      ]
    }
  },

  'lsa.lifejackets.child': {
    id: 'lsa.lifejackets.child',
    name: 'Child Life Jackets',
    category: 'LSA',
    regulation: 'WBC3 Section 14.1',
    description: 'Child life jackets when children are carried.',
    authority: 'WBC3 Section 14.1',
    placement: {
      requirements: [
        'Required when children are carried',
        'One per child plus spares',
        'Appropriate size and buoyancy'
      ]
    },
    specifications: {
      type: 'Child life jackets (appropriate buoyancy)',
      standards: 'WBC3 Section 14.1 compliance required',
      mounting: 'Accessible storage with size marking'
    },
    maintenance: {
      mandatory: [
        'Annual inspection required',
        'Monthly visual check',
        'Size verification',
        'Buoyancy system functionality'
      ]
    }
  },

  'lsa.lifejackets.spare': {
    id: 'lsa.lifejackets.spare',
    name: 'Spare Life Jackets',
    category: 'LSA',
    regulation: 'WBC3 Section 14.1',
    description: 'Spare life jackets (10% additional).',
    authority: 'WBC3 Section 14.1',
    placement: {
      requirements: [
        '10% additional life jackets required',
        'Spare equipment for emergency use',
        'Must be same standard as primary jackets'
      ]
    },
    specifications: {
      type: 'Adult spare life jackets (150N minimum)',
      standards: 'WBC3 Section 14.1 compliance required',
      mounting: 'Protected storage but accessible'
    },
    maintenance: {
      mandatory: [
        'Annual inspection required',
        'Rotation with primary stock',
        'Storage condition monitoring',
        'Same maintenance as primary jackets'
      ]
    }
  },

  // Life Buoys
  'lsa.lifebuoys.standard': {
    id: 'lsa.lifebuoys.standard',
    name: 'Life Buoys',
    category: 'LSA',
    regulation: 'WBC3 Section 14.2',
    description: 'Life buoys for man overboard recovery.',
    authority: 'WBC3 Section 14.2',
    placement: {
      requirements: [
        'Minimum number based on vessel length',
        'Must be readily accessible',
        'Distribution around vessel perimeter'
      ]
    },
    specifications: {
      type: 'Life buoys with line and light',
      standards: 'WBC3 Section 14.2 compliance required',
      mounting: 'Quick release mounting systems'
    },
    maintenance: {
      mandatory: [
        'Monthly visual inspection',
        'Light battery and operation test',
        'Line condition and attachment check',
        'Quick release mechanism operation'
      ]
    }
  },

  // Life Rafts
  'lsa.liferafts.cat0': {
    id: 'lsa.liferafts.cat0',
    name: 'Life Rafts (Category 0)',
    category: 'LSA',
    regulation: 'WBC3 Category 0 Requirements',
    description: 'Life rafts for Category 0 unrestricted operations.',
    authority: 'WBC3 Category 0 Requirements',
    placement: {
      requirements: [
        'Required for Category 0 operations',
        'Capacity for all persons on board',
        'Float-free installation preferred'
      ]
    },
    specifications: {
      type: 'Category 0 life rafts',
      standards: 'WBC3 Category 0 compliance required',
      mounting: 'Float-free or readily accessible'
    },
    maintenance: {
      mandatory: [
        'Annual service at approved facility',
        'Monthly visual inspection',
        'Hydrostatic release unit check',
        'Service expiry date monitoring'
      ]
    }
  },

  'lsa.liferafts.cat1.pob16plus': {
    id: 'lsa.liferafts.cat1.pob16plus',
    name: 'Life Rafts (Category 1, 16+ POB)',
    category: 'LSA',
    regulation: 'WBC3 Category 1 Requirements',
    description: 'Life rafts for Category 1 operations with 16 or more persons.',
    authority: 'WBC3 Category 1 Requirements',
    placement: {
      requirements: [
        'Required for Category 1 operations with 16+ POB',
        'Capacity for all persons on board',
        'Enhanced equipment requirements'
      ]
    },
    specifications: {
      type: 'Category 1 life rafts (16+ POB specification)',
      standards: 'WBC3 Category 1 compliance required',
      mounting: 'Float-free or readily accessible'
    },
    maintenance: {
      mandatory: [
        'Annual service at approved facility',
        'Monthly visual inspection',
        'Equipment inventory verification',
        'Capacity verification for POB'
      ]
    }
  },

  'lsa.liferafts.cat1.pobunder16': {
    id: 'lsa.liferafts.cat1.pobunder16',
    name: 'Life Rafts (Category 1, <16 POB)',
    category: 'LSA',
    regulation: 'WBC3 Category 1 Requirements',
    description: 'Life rafts for Category 1 operations with under 16 persons.',
    authority: 'WBC3 Category 1 Requirements',
    placement: {
      requirements: [
        'Required for Category 1 operations with <16 POB',
        'Alternative requirements to large capacity rafts',
        'Capacity for all persons on board'
      ]
    },
    specifications: {
      type: 'Category 1 life rafts (<16 POB specification)',
      standards: 'WBC3 Category 1 compliance required',
      mounting: 'Readily accessible storage'
    },
    maintenance: {
      mandatory: [
        'Annual service at approved facility',
        'Monthly visual inspection',
        'Equipment suitability verification',
        'Storage condition monitoring'
      ]
    }
  },

  'lsa.liferafts.cat2to6': {
    id: 'lsa.liferafts.cat2to6',
    name: 'Life Rafts (Categories 2-6)',
    category: 'LSA',
    regulation: 'WBC3 Category 2-6 Requirements',
    description: 'Life rafts for Category 2-6 coastal operations.',
    authority: 'WBC3 Category 2-6 Requirements',
    placement: {
      requirements: [
        'Required for Categories 2-6 operations',
        'Coastal operation specifications',
        'Capacity for all persons on board'
      ]
    },
    specifications: {
      type: 'Category 2-6 life rafts',
      standards: 'WBC3 Category 2-6 compliance required',
      mounting: 'Accessible storage appropriate for category'
    },
    maintenance: {
      mandatory: [
        'Annual service at approved facility',
        'Monthly visual inspection',
        'Category-appropriate equipment check',
        'Coastal operation suitability verification'
      ]
    }
  },

  // Pyrotechnics
  'lsa.pyrotechnics.flares': {
    id: 'lsa.pyrotechnics.flares',
    name: 'Pyrotechnic Flares',
    category: 'LSA',
    regulation: 'WBC3 Section 14.4',
    description: 'Pyrotechnic distress signals and flares.',
    authority: 'WBC3 Section 14.4',
    placement: {
      requirements: [
        'Minimum number based on area category',
        'Mix of hand flares and parachute rockets',
        'Weather protected storage required'
      ]
    },
    specifications: {
      type: 'Pyrotechnic distress signals',
      standards: 'WBC3 Section 14.4 compliance required',
      mounting: 'Weather protected accessible storage'
    },
    maintenance: {
      mandatory: [
        'Expiry date monitoring',
        'Weather protection verification',
        'Accessibility confirmation',
        'Replacement when expired'
      ]
    }
  },

  // Recovery Equipment
  'lsa.recovery.equipment': {
    id: 'lsa.recovery.equipment',
    name: 'Recovery Equipment',
    category: 'LSA',
    regulation: 'WBC3 Section 14.3',
    description: 'Equipment for man overboard recovery operations.',
    authority: 'WBC3 Section 14.3',
    placement: {
      requirements: [
        'Man overboard recovery capability',
        'Ladder or lifting equipment',
        'Must be readily deployable'
      ]
    },
    specifications: {
      type: 'MOB recovery equipment (ladder/lift)',
      standards: 'WBC3 Section 14.3 compliance required',
      mounting: 'Readily deployable from deck'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Deployment mechanism check',
        'Structural integrity inspection',
        'Crew familiarization verification'
      ]
    }
  },

  // EPIRBs
  'lsa.epirb.cat0': {
    id: 'lsa.epirb.cat0',
    name: 'EPIRB (Category 0)',
    category: 'LSA',
    regulation: 'WBC3 Category 0 Requirements',
    description: 'Emergency Position Indicating Radio Beacon for Category 0 operations.',
    authority: 'WBC3 Category 0 Requirements',
    placement: {
      requirements: [
        'Required for Category 0 operations',
        'Float-free installation required',
        'Automatic activation capability'
      ]
    },
    specifications: {
      type: 'EPIRB 406MHz with GPS',
      standards: 'WBC3 Category 0 compliance required',
      mounting: 'Float-free hydrostatic release'
    },
    maintenance: {
      mandatory: [
        'Annual inspection and test',
        'Battery expiry monitoring',
        'Float-free mechanism verification',
        'GPS functionality test'
      ]
    }
  },

  'lsa.epirb.cat1to2': {
    id: 'lsa.epirb.cat1to2',
    name: 'EPIRB (Categories 1-2)',
    category: 'LSA',
    regulation: 'WBC3 Category 1-2 Requirements',
    description: 'Emergency Position Indicating Radio Beacon for Category 1-2 operations.',
    authority: 'WBC3 Category 1-2 Requirements',
    placement: {
      requirements: [
        'Required for Category 1-2 operations',
        'Manual or float-free activation',
        'Enhanced capability for offshore operations'
      ]
    },
    specifications: {
      type: 'EPIRB 406MHz with GPS',
      standards: 'WBC3 Category 1-2 compliance required',
      mounting: 'Float-free or readily accessible'
    },
    maintenance: {
      mandatory: [
        'Annual inspection and test',
        'Battery expiry monitoring',
        'Activation mechanism verification',
        'GPS and transmission test'
      ]
    }
  },

  // SART
  'lsa.sart': {
    id: 'lsa.sart',
    name: 'Search and Rescue Transponder (LSA)',
    category: 'LSA',
    regulation: 'WBC3 Section 14.5',
    description: 'Search and Rescue Transponder for location assistance.',
    authority: 'WBC3 Section 14.5',
    placement: {
      requirements: [
        'Required for certain categories and operations',
        'Portable for use in survival craft',
        'Radar detection enhancement'
      ]
    },
    specifications: {
      type: 'SART radar transponder',
      standards: 'WBC3 Section 14.5 compliance required',
      mounting: 'Portable with survival craft compatibility'
    },
    maintenance: {
      mandatory: [
        'Annual battery and operation test',
        'Radar response verification',
        'Storage condition monitoring',
        'Crew training on operation'
      ]
    }
  },

  // Thermal Protection
  'lsa.tpa.cat6.cold': {
    id: 'lsa.tpa.cat6.cold',
    name: 'Thermal Protection (Cat 6, Cold Waters)',
    category: 'LSA',
    regulation: 'WBC3 Category 6 Requirements',
    description: 'Thermal protection aids for Category 6 operations in cold waters.',
    authority: 'WBC3 Category 6 Requirements',
    placement: {
      requirements: [
        'Required for Category 6 cold water operations',
        'Hypothermia prevention',
        'Readily accessible storage'
      ]
    },
    specifications: {
      type: 'Thermal protection aids',
      standards: 'WBC3 Category 6 cold water compliance',
      mounting: 'Readily accessible weatherproof storage'
    },
    maintenance: {
      mandatory: [
        'Annual inspection for damage',
        'Storage condition verification',
        'Material integrity check',
        'Accessibility confirmation'
      ]
    }
  },

  'lsa.tpa.cat6.openraft': {
    id: 'lsa.tpa.cat6.openraft',
    name: 'Thermal Protection (Cat 6, Open Raft)',
    category: 'LSA',
    regulation: 'WBC3 Category 6 Requirements',
    description: 'Thermal protection aids for Category 6 open life raft operations.',
    authority: 'WBC3 Category 6 Requirements',
    placement: {
      requirements: [
        'Required for Category 6 open raft operations',
        'Enhanced protection for open exposure',
        'Compatible with raft deployment'
      ]
    },
    specifications: {
      type: 'Enhanced thermal protection aids',
      standards: 'WBC3 Category 6 open raft compliance',
      mounting: 'Raft-compatible storage'
    },
    maintenance: {
      mandatory: [
        'Annual inspection for damage',
        'Raft compatibility verification',
        'Material condition assessment',
        'Deployment accessibility check'
      ]
    }
  },

  'lsa.tpa.cats0to5': {
    id: 'lsa.tpa.cats0to5',
    name: 'Thermal Protection (Categories 0-5)',
    category: 'LSA',
    regulation: 'WBC3 Category 0-5 Requirements',
    description: 'Thermal protection aids for Categories 0-5 operations.',
    authority: 'WBC3 Category 0-5 Requirements',
    placement: {
      requirements: [
        'Required for Categories 0-5 operations',
        'Survival enhancement in cold conditions',
        'Life raft compatible'
      ]
    },
    specifications: {
      type: 'Thermal protection aids',
      standards: 'WBC3 Category 0-5 compliance required',
      mounting: 'Life raft accessible storage'
    },
    maintenance: {
      mandatory: [
        'Annual inspection for damage',
        'Life raft compatibility check',
        'Storage condition monitoring',
        'Material degradation assessment'
      ]
    }
  },

  // Immersion Suits
  'lsa.immersion.suits': {
    id: 'lsa.immersion.suits',
    name: 'Immersion Suits',
    category: 'LSA',
    regulation: 'WBC3 Immersion Suit Requirements',
    description: 'Immersion suits for enhanced survival in cold water.',
    authority: 'WBC3 Immersion Suit Requirements',
    placement: {
      requirements: [
        'Required for certain operations and areas',
        'Individual suit for each crew member',
        'Size-appropriate fitting required'
      ]
    },
    specifications: {
      type: 'Immersion suits with integrated flotation',
      standards: 'WBC3 immersion suit compliance required',
      mounting: 'Individual storage with size marking'
    },
    maintenance: {
      mandatory: [
        'Annual inspection and test',
        'Waterproof integrity verification',
        'Flotation capability test',
        'Size and fit verification'
      ]
    }
  },

  // Alarms
  'lsa.general.alarm.pob': {
    id: 'lsa.general.alarm.pob',
    name: 'General Alarm (Person Overboard)',
    category: 'LSA',
    regulation: 'WBC3 Section 14.6',
    description: 'General alarm system for person overboard emergencies.',
    authority: 'WBC3 Section 14.6',
    placement: {
      requirements: [
        'Audible throughout vessel',
        'Distinct MOB signal',
        'Activatable from control position'
      ]
    },
    specifications: {
      type: 'General alarm system with MOB signal',
      standards: 'WBC3 Section 14.6 compliance required',
      mounting: 'Control position with vessel coverage'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Coverage area verification',
        'Signal distinctiveness check',
        'Power source reliability test'
      ]
    }
  },

  'lsa.general.alarm.power': {
    id: 'lsa.general.alarm.power',
    name: 'General Alarm (Power Supply)',
    category: 'LSA',
    regulation: 'WBC3 Section 14.6',
    description: 'Power supply system for general alarm operation.',
    authority: 'WBC3 Section 14.6',
    placement: {
      requirements: [
        'Reliable power supply for alarm system',
        'Battery backup recommended',
        'Independent from main power if possible'
      ]
    },
    specifications: {
      type: 'Alarm power supply with backup',
      standards: 'WBC3 Section 14.6 compliance required',
      mounting: 'Protected power supply installation'
    },
    maintenance: {
      mandatory: [
        'Monthly power supply test',
        'Battery backup verification',
        'Charging system operation check',
        'Power independence verification'
      ]
    }
  },

  // PA System
  'lsa.pa.system': {
    id: 'lsa.pa.system',
    name: 'Public Address System',
    category: 'LSA',
    regulation: 'WBC3 Section 14.7',
    description: 'Public address system for emergency communications.',
    authority: 'WBC3 Section 14.7',
    placement: {
      requirements: [
        'Clear communication throughout vessel',
        'Emergency instruction capability',
        'Weather protected installation'
      ]
    },
    specifications: {
      type: 'PA system with emergency override',
      standards: 'WBC3 Section 14.7 compliance required',
      mounting: 'Protected installation with vessel coverage'
    },
    maintenance: {
      mandatory: [
        'Monthly operation and clarity test',
        'Coverage area verification',
        'Emergency override function test',
        'Weather protection integrity check'
      ]
    }
  },

  // EQUIPMENT REGISTER SPECIFIC MAPPINGS (These are the actual names appearing in Equipment Register)
  
  // Personal Flotation Devices (from Equipment Register debug output)
  'Personal Flotation Devices': {
    id: 'Personal Flotation Devices',
    name: 'Personal Flotation Devices',
    category: 'LSA',
    regulation: 'WBC3 Section 14.1',
    description: 'Personal flotation devices for crew and passengers safety.',
    authority: 'WBC3 Section 14.1',
    placement: {
      requirements: [
        'One per person on board plus spares',
        'Must be readily accessible',
        'Appropriate type for vessel operations'
      ]
    },
    specifications: {
      type: 'Personal flotation devices (PFDs)',
      standards: 'WBC3 Section 14.1 compliance required',
      mounting: 'Readily accessible storage locations'
    },
    maintenance: {
      mandatory: [
        'Annual inspection required',
        'Monthly visual check',
        'Flotation system verification',
        'Strap and buckle functionality'
      ]
    }
  },

  // Lifebuoys (from Equipment Register debug output - first entry without guidance)
  'Lifebuoys': {
    id: 'Lifebuoys',
    name: 'Lifebuoys',
    category: 'LSA',
    regulation: 'WBC3 Section 14.2',
    description: 'Lifebuoys for man overboard recovery and emergency flotation.',
    authority: 'WBC3 Section 14.2',
    placement: {
      requirements: [
        'Minimum number based on vessel length',
        'Must be readily accessible',
        'Distribution around vessel perimeter'
      ]
    },
    specifications: {
      type: 'Lifebuoys with line and light',
      standards: 'WBC3 Section 14.2 compliance required',
      mounting: 'Quick release mounting systems'
    },
    maintenance: {
      mandatory: [
        'Monthly visual inspection',
        'Light battery and operation test',
        'Line condition and attachment check',
        'Quick release mechanism operation'
      ]
    }
  },

  // Universal MOB Recovery Equipment (from Equipment Register debug output)
  'Universal MOB Recovery Equipment': {
    id: 'Universal MOB Recovery Equipment',
    name: 'Universal MOB Recovery Equipment',
    category: 'LSA',
    regulation: 'WBC3 Section 14.3',
    description: 'Universal man overboard recovery equipment for all vessel types.',
    authority: 'WBC3 Section 14.3',
    placement: {
      requirements: [
        'Universal MOB recovery capability',
        'Suitable for all crew physical capabilities',
        'Must be readily deployable'
      ]
    },
    specifications: {
      type: 'Universal MOB recovery system',
      standards: 'WBC3 Section 14.3 compliance required',
      mounting: 'Readily deployable from multiple positions'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Deployment mechanism check',
        'Universal accessibility verification',
        'Crew training verification'
      ]
    }
  },

  // Foam Lifejackets (Inherent Buoyancy) (from Equipment Register debug output)
  'Foam Lifejackets (Inherent Buoyancy)': {
    id: 'Foam Lifejackets (Inherent Buoyancy)',
    name: 'Foam Lifejackets (Inherent Buoyancy)',
    category: 'LSA',
    regulation: 'WBC3 Section 14.1',
    description: 'Foam lifejackets with inherent buoyancy for continuous flotation.',
    authority: 'WBC3 Section 14.1',
    placement: {
      requirements: [
        'Inherent buoyancy type lifejackets',
        'No inflation required - always ready',
        'Must be readily accessible'
      ]
    },
    specifications: {
      type: 'Foam lifejackets with inherent buoyancy',
      standards: 'WBC3 Section 14.1 compliance required',
      mounting: 'Readily accessible storage with size marking'
    },
    maintenance: {
      mandatory: [
        'Annual inspection for foam integrity',
        'Monthly visual check for damage',
        'Strap and buckle functionality',
        'Buoyancy material condition assessment'
      ]
    }
  },

  // Missing IDs identified by audit - from RA verified guidance
  
  'basic_lifejackets': {
    id: 'basic_lifejackets',
    name: 'Lifejackets (Adult)',
    category: 'LSA',
    regulation: 'WBC3 14.4.4',
    description: 'A suitable lifejacket shall be provided for each person on board',
    authority: 'WBC3 14.4.1, 14.4.2, 14.4.3, 14.4.4 | SOLAS III/33',
    placement: {
      requirements: [
        'Readily accessible to all crew and passengers (WBC3 14.4.1)',
        'Stowed so that lifejackets can be put on in the minimum of time (WBC3 14.4.1)'
      ]
    },
    specifications: {
      type: 'SOLAS approved lifejacket or equivalent standard approved by Administration',
      standards: 'SOLAS regulation III/33 or equivalent approved by Administration (WBC3 14.4.1)',
      features: 'Fitted with whistle and retro-reflective materials (WBC3 14.4.2)',
      lighting: 'Must be fitted with light for area categories 0, 1, 2, 3, or 5 (WBC3 14.4.3)',
      certification: 'SOLAS approved or Administration approved equivalent'
    },
    maintenance: {
      mandatory: [
        'Annual inspection and servicing (WBC3 14.9 references to manufacturer requirements)',
        'Inflatable lifejackets: annual service including inflation test and CO2 cylinder replacement'
      ],
      recommended: [
        'Monthly visual inspection for damage, fading, or wear',
        'Check whistle operation and retro-reflective tape condition',
        'Verify light operation (where fitted)'
      ]
    }
  },

  'basic_lifebuoy': {
    id: 'basic_lifebuoy',
    name: 'Lifebuoys',
    category: 'LSA',
    regulation: 'WBC3 14.3',
    description: 'Standard lifebuoys with required quantities per Table 14.1.2',
    authority: 'WBC3 14.3.1, Table 14.1.2 | SOLAS III/7.1',
    placement: {
      requirements: [
        'Readily accessible on deck areas (WBC3 14.3)',
        'Distributed around vessel to ensure accessibility from both sides'
      ]
    },
    specifications: {
      type: 'Solid buoyant lifebuoy - not inflatable type',
      standards: 'SOLAS regulation III/7.1 as referenced in WBC3 14.3',
      quantity: 'Less than 16 persons: 2 total; 16 or more persons: 4 total (WBC3 Table 14.1.2)',
      marking: 'Two means of identifying the vessel including vessel name (WBC3 14.3.1.2)',
      lighting: 'Fitted with lights for area categories 0, 1, 2, 3, and 5 (WBC3 14.3.1.3)'
    },
    maintenance: {
      mandatory: [
        'Annual inspection for damage and buoyancy (WBC3 14.9)',
        'Light battery replacement as per manufacturer requirements'
      ],
      recommended: [
        'Monthly visual inspection for damage and securing arrangement',
        'Check light operation where fitted',
        'Verify buoyant line condition and attachment'
      ]
    }
  },

  'lsa.general.alarm': {
    id: 'lsa.general.alarm',
    name: 'General Alarm (POB)',
    category: 'LSA',
    regulation: 'WBC3 14.6.1',
    description: 'A vessel shall have a general alarm where it carries 16 or more persons on board',
    authority: 'WBC3 14.6.1, 14.6.2',
    placement: {
      requirements: [
        'Audible in all parts of vessel (WBC3 14.6.2)',
        'Control accessible from bridge/wheelhouse',
        'Protected from accidental activation'
      ]
    },
    specifications: {
      type: 'General alarm system for POB trigger',
      standards: 'Audible general alarm system per WBC3 14.6',
      trigger: 'Categories 0, 1, or 2 with 16 or more persons on board (WBC3 14.6.1)',
      coverage: 'Audible in all parts of vessel (WBC3 14.6.2)',
      activation: 'Manual activation from control position'
    },
    maintenance: {
      mandatory: [
        'Annual functional testing of alarm system',
        'Battery backup testing where fitted',
        'Audibility verification in all vessel areas'
      ],
      recommended: [
        'Monthly activation test and sound level check',
        'Check wiring and connections for corrosion',
        'Verify crew familiarity with activation procedures',
        'Test backup power supply if fitted'
      ]
    }
  }
};

/**
 * Simple Direct Lookup - No mappings, no complexity
 * @param {string} itemId - Equipment requirement ID
 * @returns {Object|null} Guidance object or null
 */
export function getLSASimpleGuidance(itemId) {
  return LSA_SIMPLE_GUIDANCE[itemId] || null;
}